import { useTranslation } from "react-i18next";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import type { CredentialResultsDialogProps } from "./CredentialResultsDialog.types";
import { ErrorBox, SuccessBox } from "./CredentialResultsDialog.styles";

const CredentialResultsDialog = ({
    open,
    results,
    onClose,
}: CredentialResultsDialogProps) => {
    const { t } = useTranslation();
    const handleCloseDialog = () => {
        onClose();
    };

    const successResults = results?.successful;
    const groupedSuccessResultsBySubjectDid = successResults?.reduce<
        Record<string, NonNullable<typeof successResults>>
    >((acc, item) => {
        const { subjectDid } = item;

        if (!acc[subjectDid]) {
            acc[subjectDid] = [];
        }

        acc[subjectDid].push(item);

        return acc;
    }, {});

    const failedResults = results?.unsuccessful;
    const groupedFailedResultsBySubjectDid = failedResults?.reduce<
        Record<string, NonNullable<typeof failedResults>>
    >((acc, item) => {
        const { subjectDid } = item;

        if (!acc[subjectDid]) {
            acc[subjectDid] = [];
        }

        acc[subjectDid].push(item);

        return acc;
    }, {});

    return (
        <Dialog
            onClose={handleCloseDialog}
            open={open}
            maxWidth={"md"}
            fullWidth
            data-testid="result"
        >
            <DialogTitle>
                <Typography>{t("credentials-results-dialog.title")}</Typography>
            </DialogTitle>
            <DialogContent>
                {successResults?.length ? (
                    <List
                        subheader={
                            <SuccessBox>
                                <strong>
                                    {t(
                                        "credentials-results-dialog.successfull-results"
                                    )}
                                </strong>
                            </SuccessBox>
                        }
                    >
                        {groupedSuccessResultsBySubjectDid &&
                            Object.entries(
                                groupedSuccessResultsBySubjectDid
                            ).map(([subjectDid, items]) => (
                                <div
                                    key={subjectDid}
                                    style={{ marginBlock: 12 }}
                                >
                                    <ListItem
                                        disablePadding
                                        style={{ fontSize: 14 }}
                                    >
                                        <strong>{subjectDid}</strong>
                                    </ListItem>
                                    {items.map((item, itemIndex) => (
                                        <ListItem
                                            disablePadding
                                            key={`${subjectDid}-${itemIndex}`}
                                        >
                                            {item.indicator.name}
                                        </ListItem>
                                    ))}
                                </div>
                            ))}
                    </List>
                ) : null}
                {failedResults?.length ? (
                    <List
                        subheader={
                            <ErrorBox>
                                <strong>
                                    {t(
                                        "credentials-results-dialog.unsuccessfull-results"
                                    )}
                                </strong>
                            </ErrorBox>
                        }
                    >
                        {groupedFailedResultsBySubjectDid &&
                            Object.entries(
                                groupedFailedResultsBySubjectDid
                            ).map(([subjectDid, items]) => (
                                <div
                                    key={subjectDid}
                                    style={{ marginBlock: 12 }}
                                >
                                    <ListItem
                                        disablePadding
                                        style={{ fontSize: 14 }}
                                    >
                                        <strong>{subjectDid}</strong>
                                    </ListItem>
                                    {items.map((item, itemIndex) => (
                                        <ListItem
                                            disablePadding
                                            key={`${subjectDid}-${itemIndex}`}
                                        >
                                            {item.indicator.name}
                                        </ListItem>
                                    ))}
                                </div>
                            ))}
                    </List>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog}>{t("general.oke")}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CredentialResultsDialog;
